import React, { useState, useEffect, useRef, useCallback } from 'react';
import SessionManager from '../routes/SessionManager';
import aiLogo from '../assets/img/ai_assistant_square.png';

const CHATBOT_WS_URL = `wss://www.sitesupt.com/ws/`;

export default function ChatbotWidget() {
    const sessionManager = new SessionManager();
    const user = sessionManager.readCookie('user', false);

    // Initialize isOpen from localStorage
    const [isOpen, setIsOpen] = useState(() => {
        try {
            const stored = localStorage.getItem('chatbot_is_open');
            return stored === 'true';
        } catch {
            return false;
        }
    });
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [wsStatus, setWsStatus] = useState('disconnected'); // 'connecting' | 'verifying' | 'ready' | 'error' | 'reconnecting'

    // Widget positioning and sizing state
    const [isMaximized, setIsMaximized] = useState(false);
    const [isPinned, setIsPinned] = useState(false);
    const [position, setPosition] = useState(() => {
        try {
            const stored = localStorage.getItem('chatbot_position');
            return stored ? JSON.parse(stored) : { bottom: 86, right: 24 };
        } catch {
            return { bottom: 86, right: 24 };
        }
    });
    const [size, setSize] = useState(() => {
        try {
            const stored = localStorage.getItem('chatbot_size');
            return stored ? JSON.parse(stored) : { width: 340, height: 480 };
        } catch {
            return { width: 340, height: 480 };
        }
    });

    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [resizeDirection, setResizeDirection] = useState(null);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

    const wsRef = useRef(null);
    const sessionIdRef = useRef(user && user.chatbot_session_id ? user.chatbot_session_id : null); // Try to reuse stored session_id
    const messagesEndRef = useRef(null);
    const pendingTasksRef = useRef(new Map());
    const reconnectTimerRef = useRef(null);
    const mountedRef = useRef(true);
    const isOpenRef = useRef(false);

    const username = user && user.user_login;
    const userToken = user && user.user_token;

    // Store session_id in user cookie
    const storeSessionId = useCallback((sessionId) => {
        if (sessionId) {
            const updatedUser = { ...user, chatbot_session_id: sessionId };
            sessionManager.storeCookie('user', updatedUser);
            sessionIdRef.current = sessionId;
        }
    }, [user, sessionManager]);

    // Clear session_id from user cookie
    const clearSessionId = useCallback(() => {
        if (user && user.chatbot_session_id) {
            const updatedUser = { ...user };
            delete updatedUser.chatbot_session_id;
            sessionManager.storeCookie('user', updatedUser);
        }
        sessionIdRef.current = null;
    }, [user, sessionManager]);

    const appendMessage = useCallback((role, text, isNotification = false) => {
        setMessages(prev => [...prev, {
            id: crypto.randomUUID(),
            role,
            text,
            isNotification,
            timestamp: new Date()
        }]);
    }, []);

    // Format timestamp - show time only if today, otherwise show date + time
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '';

        const date = new Date(timestamp);
        const now = new Date();
        const isToday = date.toDateString() === now.toDateString();

        const timeStr = date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });

        if (isToday) {
            return timeStr;
        } else {
            const dateStr = date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
            });
            return `${dateStr}, ${timeStr}`;
        }
    };

    // Helper to render message text with clickable links
    const renderMessageText = (text, isNotification = false) => {
        if (!text) return null;

        // URL regex pattern
        const urlPattern = /(https?:\/\/[^\s]+)/g;
        const parts = text.split(urlPattern);

        return parts.map((part, index) => {
            if (part.match(urlPattern)) {
                return (
                    <a
                        key={index}
                        href={part}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            color: '#1976d2',
                            textDecoration: 'underline',
                            fontWeight: 500,
                            display: 'block',
                            marginTop: '6px',
                            wordBreak: 'break-all',
                        }}
                    >
                        {part}
                    </a>
                );
            }
            return <span key={index}>{part}</span>;
        });
    };

    const sendRaw = useCallback((payload) => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify(payload));
        }
    }, []);

    const verifyUsername = useCallback(() => {
        return new Promise((resolve) => {
            const taskId = crypto.randomUUID();
            pendingTasksRef.current.set(taskId, resolve);
            sendRaw({ type: 'verify_username', task_id: taskId, username, user_token: userToken });
        });
    }, [username, userToken, sendRaw]);

    const resumeSession = useCallback((sessionId) => {
        return new Promise((resolve) => {
            const taskId = crypto.randomUUID();
            pendingTasksRef.current.set(taskId, resolve);
            sendRaw({ type: 'resume_session', task_id: taskId, session_id: sessionId, username });
        });
    }, [username, sendRaw]);

    const loadHistory = useCallback(() => {
        if (!sessionIdRef.current) return Promise.resolve({ success: false, history: [] });
        return new Promise((resolve) => {
            const taskId = crypto.randomUUID();
            pendingTasksRef.current.set(taskId, resolve);
            sendRaw({ type: 'load_history', task_id: taskId, session_id: sessionIdRef.current, username });
        });
    }, [username, sendRaw]);

    const clearHistory = useCallback(async () => {
        if (!sessionIdRef.current || wsStatus !== 'ready') return;

        console.log('Clearing chat history...');
        const taskId = crypto.randomUUID();
        const responsePromise = new Promise((resolve) => {
            pendingTasksRef.current.set(taskId, resolve);
        });

        sendRaw({ type: 'clear_history', task_id: taskId, session_id: sessionIdRef.current, username });

        const response = await responsePromise;
        console.log('Clear history response:', response);
        if (response && response.success) {
            setMessages([]); // Clear UI
            console.log('Chat history cleared successfully');
        } else {
            console.error('Failed to clear chat history:', response);
        }
    }, [username, wsStatus, sendRaw]);


    const connect = useCallback(() => {
        console.log('Attempting to connect to chatbot WebSocket...', username);
        if (!username) return;

        setWsStatus('connecting');
        const ws = new WebSocket(CHATBOT_WS_URL);
        wsRef.current = ws;

        ws.addEventListener('open', async () => {
            if (!mountedRef.current) return;

            // If we have a stored session_id, try to resume
            if (sessionIdRef.current) {
                console.log('Attempting to resume session:', sessionIdRef.current);
                setWsStatus('verifying');
                const resumeResult = await resumeSession(sessionIdRef.current);
                if (!mountedRef.current) return;

                if (resumeResult && resumeResult.success) {
                    console.log('Session resumed successfully');
                    setWsStatus('ready');

                    // Load history for UI display (returns in-memory data when available)
                    const historyResult = await loadHistory();
                    console.log('Load history result after resume_session:', historyResult);
                    if (historyResult && historyResult.success && historyResult.history.length > 0) {
                        console.log('Loading', historyResult.history.length, 'messages from history');
                        setMessages(historyResult.history.map(h => ({
                            id: crypto.randomUUID(),
                            role: h.role === 'user' ? 'user' : 'bot',
                            text: h.text,
                            isNotification: h.role === 'assistant' && (
                                h.text.includes('is ready:') ||
                                h.text.includes('has been completed') ||
                                h.text.includes('has finished')
                            ),
                            timestamp: h.timestamp ? new Date(h.timestamp) : null
                        })));
                    }
                    return;
                }

                console.log('Session resume failed, falling back to verify_username');
                clearSessionId();
            }

            // Verify username to get new session_id
            setWsStatus('verifying');
            const result = await verifyUsername();
            console.log('Username verification result:', result);
            if (!mountedRef.current) return;

            if (result && result.exists) {
                storeSessionId(result.session_id);
                setWsStatus('ready');

                // Load history for UI display after new authentication
                const historyResult = await loadHistory();
                if (historyResult && historyResult.success && historyResult.history.length > 0) {
                    setMessages(historyResult.history.map(h => ({
                        id: crypto.randomUUID(),
                        role: h.role === 'user' ? 'user' : 'bot',
                        text: h.text,
                        isNotification: h.role === 'assistant' && (
                            h.text.includes('is ready:') ||
                            h.text.includes('has been completed') ||
                            h.text.includes('has finished')
                        ),
                        timestamp: h.timestamp ? new Date(h.timestamp) : null
                    })));
                }
            } else {
                setWsStatus('error');
                appendMessage('bot', 'Unable to authenticate. Please contact support.');
            }
        });

        ws.addEventListener('message', (event) => {
            if (!mountedRef.current) return;
            let msg;
            try { msg = JSON.parse(event.data); } catch { return; }

            // Resolve pending task promises
            if (msg.task_id && pendingTasksRef.current.has(msg.task_id)) {
                const resolve = pendingTasksRef.current.get(msg.task_id);
                pendingTasksRef.current.delete(msg.task_id);

                if (msg.type === 'verify_username_response') {
                    resolve({ exists: msg.exists, session_id: msg.session_id });
                } else if (msg.type === 'resume_session_response') {
                    resolve({ success: msg.success, session_id: msg.session_id });
                } else if (msg.type === 'session_history_response') {
                    resolve({ success: msg.success, history: msg.history || [] });
                } else if (msg.type === 'clear_history_response') {
                    resolve({ success: msg.success });
                } else if (msg.type === 'task_response') {
                    resolve(msg);
                }
                return;
            }

            // Handle unprompted server messages
            if (msg.type === 'shutdown') {
                setWsStatus('reconnecting');
                clearSessionId(); // Clear session_id on shutdown
                appendMessage('bot', 'Connection interrupted. Reconnecting...');
                reconnectTimerRef.current = setTimeout(() => {
                    if (mountedRef.current && isOpenRef.current) connect();
                }, 3000);
            }

            if (msg.type === 'chat_notification') {
                // Server-initiated notification (e.g., async operation completed)
                if (msg.session_id === sessionIdRef.current) {
                    appendMessage('bot', msg.response, true);
                }
                return;
            }

            if (msg.type === 'error') {
                appendMessage('bot', `Server error: ${msg.error}`);
            }
        });

        ws.addEventListener('close', () => {
            if (!mountedRef.current) return;
            // Don't clear session_id on disconnect - it may still be valid for reconnection
            if (wsStatus !== 'error' && isOpenRef.current) {
                setWsStatus('reconnecting');
                reconnectTimerRef.current = setTimeout(() => {
                    if (mountedRef.current && isOpenRef.current) connect();
                }, 4000);
            } else if (!isOpenRef.current) {
                setWsStatus('disconnected');
            }
        });

        ws.addEventListener('error', () => {
            if (!mountedRef.current) return;
            setWsStatus('error');
        });
    }, [username, verifyUsername, resumeSession, loadHistory, appendMessage, wsStatus, storeSessionId, clearSessionId]);

    // Connect/disconnect based on widget open state
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            clearTimeout(reconnectTimerRef.current);
            if (wsRef.current) wsRef.current.close(1000);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Persist isOpen state to localStorage
    useEffect(() => {
        try {
            localStorage.setItem('chatbot_is_open', isOpen.toString());
        } catch (e) {
            console.warn('Failed to persist chatbot state:', e);
        }
    }, [isOpen]);

    // Persist position and size
    useEffect(() => {
        try {
            localStorage.setItem('chatbot_position', JSON.stringify(position));
        } catch (e) {
            console.warn('Failed to persist position:', e);
        }
    }, [position]);

    useEffect(() => {
        try {
            localStorage.setItem('chatbot_size', JSON.stringify(size));
        } catch (e) {
            console.warn('Failed to persist size:', e);
        }
    }, [size]);

    useEffect(() => {
        isOpenRef.current = isOpen;
        if (isOpen) {
            const state = wsRef.current ? wsRef.current.readyState : WebSocket.CLOSED;
            if (state === WebSocket.CLOSED || state === WebSocket.CLOSING) {
                connect();
            }
        } else {
            clearTimeout(reconnectTimerRef.current);
            if (wsRef.current) {
                wsRef.current.close(1000);
                wsRef.current = null;
            }
            setWsStatus('disconnected');
        }
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // Scroll to bottom when messages change or panel opens
    useEffect(() => {
        if (isOpen) {
            if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isOpen]);

    const sendMessage = useCallback(async () => {
        const text = input.trim();
        if (!text || wsStatus !== 'ready' || !sessionIdRef.current) return;

        setInput('');
        appendMessage('user', text);

        const taskId = crypto.randomUUID();
        const responsePromise = new Promise((resolve) => {
            pendingTasksRef.current.set(taskId, resolve);
        });

        sendRaw({
            type: 'task_request',
            task_id: taskId,
            session_id: sessionIdRef.current,
            username,
            message: text,
        });

        // Show typing indicator
        const typingId = crypto.randomUUID();
        setMessages(prev => [...prev, { id: typingId, role: 'bot', text: null, typing: true }]);

        const response = await responsePromise;
        setMessages(prev => prev.filter(m => m.id !== typingId));

        if (response && response.success) {
            appendMessage('bot', response.response);
        } else {
            const errorMsg = (response && response.response) || 'Something went wrong. Please try again.';
            appendMessage('bot', errorMsg);

            // Check for session-related errors
            if (errorMsg.includes('Session expired') || errorMsg.includes('No active session') || errorMsg.includes('re-authenticate')) {
                clearSessionId(); // Clear invalid session
                setWsStatus('reconnecting');
                setTimeout(() => {
                    if (mountedRef.current && isOpenRef.current) connect();
                }, 2000);
            }
        }
    }, [input, wsStatus, username, sendRaw, appendMessage, clearSessionId]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    // Dragging handlers
    const handleMouseDownDrag = useCallback((e) => {
        if (isPinned || isMaximized) return;
        e.preventDefault(); // Prevent text selection
        setIsDragging(true);

        // Calculate offset from the panel's bottom-right corner
        // This works correctly with bottom/right CSS positioning
        setDragOffset({
            x: (window.innerWidth - position.right) - e.clientX,
            y: (window.innerHeight - position.bottom) - e.clientY,
        });
    }, [isPinned, isMaximized, position]);

    // Resizing handlers
    const handleMouseDownResize = useCallback((e, direction) => {
        if (isMaximized) return;
        e.stopPropagation();
        setIsResizing(true);
        setResizeDirection(direction);
    }, [isMaximized]);

    const toggleMaximize = useCallback(() => {
        setIsMaximized(prev => !prev);
    }, []);

    const togglePin = useCallback(() => {
        setIsPinned(prev => !prev);
    }, []);

    // Global mouse move and up handlers
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging) {
                const newBottom = window.innerHeight - e.clientY - dragOffset.y;
                const newRight = window.innerWidth - e.clientX - dragOffset.x;

                // Ensure the chatbot stays fully within viewport on all sides
                const minBottom = 0; // Bottom edge can't go below viewport bottom
                const maxBottom = window.innerHeight - size.height; // Top edge can't go above viewport top
                const minRight = 0; // Right edge can't go beyond viewport right
                const maxRight = window.innerWidth - size.width; // Left edge can't go beyond viewport left

                setPosition({
                    bottom: Math.max(minBottom, Math.min(newBottom, maxBottom)),
                    right: Math.max(minRight, Math.min(newRight, maxRight)),
                });
            } else if (isResizing && resizeDirection) {
                const panel = document.querySelector('.chatbot-panel');
                if (!panel) return;
                const rect = panel.getBoundingClientRect();

                let newWidth = size.width;
                let newHeight = size.height;
                let newBottom = position.bottom;
                let newRight = position.right;

                if (resizeDirection.includes('left')) {
                    const deltaX = rect.right - e.clientX;
                    // Can't extend beyond left edge of viewport
                    const maxWidth = rect.right;
                    newWidth = Math.max(280, Math.min(600, Math.min(deltaX, maxWidth)));
                    newRight = window.innerWidth - rect.right;
                }
                if (resizeDirection.includes('right')) {
                    const deltaX = e.clientX - rect.left;
                    // Can't extend beyond right edge of viewport
                    const maxWidth = window.innerWidth - rect.left;
                    newWidth = Math.max(280, Math.min(600, Math.min(deltaX, maxWidth)));
                }
                if (resizeDirection.includes('top')) {
                    const deltaY = rect.bottom - e.clientY;
                    // Can't extend beyond top edge of viewport
                    const maxHeight = rect.bottom;
                    newHeight = Math.max(300, Math.min(800, Math.min(deltaY, maxHeight)));
                }
                if (resizeDirection.includes('bottom')) {
                    const deltaY = e.clientY - rect.top;
                    // Can't extend beyond bottom edge of viewport
                    const maxHeight = window.innerHeight - rect.top;
                    newHeight = Math.max(300, Math.min(800, Math.min(deltaY, maxHeight)));
                    // When resizing from bottom, adjust bottom position to keep top fixed
                    const heightDiff = newHeight - size.height;
                    newBottom = Math.max(0, position.bottom - heightDiff);
                }

                setSize({ width: newWidth, height: newHeight });
                setPosition({ bottom: newBottom, right: newRight });
            }
        };

        const handleMouseUp = () => {
            setIsDragging(false);
            setIsResizing(false);
            setResizeDirection(null);
        };

        if (isDragging || isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, isResizing, resizeDirection, dragOffset, position, size]);

    if (!username) return null;

    const statusColor = { connecting: '#ffc107', verifying: '#ffc107', ready: '#28a745', error: '#dc3545', reconnecting: '#ffc107', disconnected: '#6c757d' }[wsStatus] || '#6c757d';
    const statusLabel = { connecting: 'Connecting…', verifying: 'Authenticating…', ready: 'Online', error: 'Unavailable', reconnecting: 'Reconnecting…', disconnected: 'Offline' }[wsStatus] || '';

    return (
        <>
            {/* Floating toggle button */}
            {!isOpen && (
                <button
                    onClick={() => setIsOpen(true)}
                    title="SiteKick AI Assistant"
                    style={{
                        position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999,
                        width: '52px', height: '52px', borderRadius: '50%',
                        background: '#6b32b4', border: 'none', color: '#fff',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s',
                        fontSize: '24px',
                    }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                >
                    <i className="fa fa-comments" />
                </button>
            )}

            {/* Chat panel */}
            {isOpen && (
                <div
                    className="chatbot-panel"
                    style={{
                        position: 'fixed',
                        bottom: isMaximized ? 0 : `${position.bottom}px`,
                        right: isMaximized ? 0 : `${position.right}px`,
                        top: isMaximized ? 0 : 'auto',
                        left: isMaximized ? 0 : 'auto',
                        zIndex: isPinned ? 10000 : 9998,
                        width: isMaximized ? '100vw' : `${size.width}px`,
                        height: isMaximized ? '100vh' : `${size.height}px`,
                        background: '#fff',
                        borderRadius: isMaximized ? 0 : '12px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                        display: 'flex', flexDirection: 'column',
                        fontFamily: 'inherit', overflow: 'hidden',
                        transition: isMaximized ? 'all 0.2s ease-in-out' : 'none',
                        cursor: isDragging ? 'grabbing' : 'default',
                    }}
                >
                    {/* Header */}
                    <div
                        onMouseDown={handleMouseDownDrag}
                        style={{
                            background: '#6b32b4', color: '#fff',
                            padding: '12px 16px',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            flexShrink: 0,
                            cursor: (isPinned || isMaximized) ? 'default' : 'grab',
                            userSelect: 'none',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div className="icon-wrapper">
                                <img className='ai-icon mr-2' src={aiLogo} alt="AI Logo" />
                            </div>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: '14px', lineHeight: 1.2 }}>SiteKick AI</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '2px' }}>
                                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: statusColor, display: 'inline-block' }} />
                                    <span style={{ fontSize: '11px', opacity: 0.9 }}>{statusLabel}</span>
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <button
                                onClick={clearHistory}
                                title="Clear conversation"
                                disabled={messages.length === 0 || wsStatus !== 'ready'}
                                style={{
                                    background: 'transparent', border: 'none', color: '#fff',
                                    cursor: (messages.length === 0 || wsStatus !== 'ready') ? 'not-allowed' : 'pointer',
                                    fontSize: '15px', padding: '4px 6px',
                                    opacity: (messages.length === 0 || wsStatus !== 'ready') ? 0.4 : 0.7,
                                    transition: 'opacity 0.2s',
                                }}
                                onMouseEnter={e => {
                                    if (messages.length > 0 && wsStatus === 'ready') {
                                        e.currentTarget.style.opacity = '1';
                                    }
                                }}
                                onMouseLeave={e => {
                                    if (messages.length > 0 && wsStatus === 'ready') {
                                        e.currentTarget.style.opacity = '0.7';
                                    }
                                }}
                            >
                                <i className="fa fa-trash-alt" />
                            </button>
                            <button
                                onClick={togglePin}
                                title={isPinned ? "Unpin" : "Pin on top"}
                                style={{
                                    background: 'transparent', border: 'none', color: '#fff',
                                    cursor: 'pointer', fontSize: '15px', padding: '4px 6px',
                                    opacity: 0.7,
                                    transition: 'opacity 0.2s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                                onMouseLeave={e => e.currentTarget.style.opacity = '0.7'}
                            >
                                <i className={`fa fa-thumbtack${isPinned ? '' : ' fa-rotate-90'}`} />
                            </button>
                            <button
                                onClick={toggleMaximize}
                                title={isMaximized ? "Restore" : "Maximize"}
                                style={{
                                    background: 'transparent', border: 'none', color: '#fff',
                                    cursor: 'pointer', fontSize: '15px', padding: '4px 6px',
                                    opacity: 0.7,
                                    transition: 'opacity 0.2s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                                onMouseLeave={e => e.currentTarget.style.opacity = '0.7'}
                            >
                                <i className={`fa ${isMaximized ? 'fa-compress' : 'fa-expand'}`} />
                            </button>
                            <button
                                onClick={() => setIsOpen(false)}
                                title="Close"
                                style={{
                                    background: 'transparent', border: 'none', color: '#fff',
                                    cursor: 'pointer', fontSize: '15px', padding: '4px 6px',
                                    opacity: 0.7,
                                    transition: 'opacity 0.2s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                                onMouseLeave={e => e.currentTarget.style.opacity = '0.7'}
                            >
                                <i className="fa fa-times" />
                            </button>
                        </div>
                    </div>

                    {/* Messages */}
                    <div style={{
                        flex: 1, overflowY: 'auto', padding: '12px',
                        display: 'flex', flexDirection: 'column', gap: '8px',
                    }}>
                        {messages.length === 0 && wsStatus === 'ready' && (
                            <div style={{
                                textAlign: 'center', color: '#888', fontSize: '13px',
                                marginTop: '40px', padding: '0 16px', lineHeight: 1.6,
                            }}>
                                <i className="fa fa-comment-o" style={{ fontSize: '32px', marginBottom: '8px', display: 'block', color: '#6b32b4' }} />
                                Hi {username}! Ask me anything about your sites, zones, or activity.
                            </div>
                        )}
                        {messages.length === 0 && wsStatus !== 'ready' && (
                            <div style={{ textAlign: 'center', color: '#888', fontSize: '13px', marginTop: '60px' }}>
                                <i className="fa fa-circle-notch fa-spin" style={{ fontSize: '24px', color: '#1976d2', marginBottom: '10px', display: 'block' }} />
                                {statusLabel}
                            </div>
                        )}
                        {messages.map(msg => (
                            <div key={msg.id} style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                                gap: '2px',
                            }}>
                                <div style={{
                                    maxWidth: '82%',
                                    padding: '8px 12px',
                                    borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                                    background: msg.role === 'user' ? '#6b32b4' : msg.isNotification ? '#e3f2fd' : '#f0f2f5',
                                    color: msg.role === 'user' ? '#fff' : '#222',
                                    fontSize: '13px', lineHeight: 1.5,
                                    wordBreak: 'break-word', whiteSpace: 'pre-wrap',
                                    border: msg.isNotification ? '1px solid #90caf9' : 'none',
                                }}>
                                    {msg.typing
                                        ? <span style={{ display: 'flex', gap: '4px', alignItems: 'center', padding: '2px 4px' }}>
                                            {[0, 1, 2].map(i => (
                                                <span key={i} style={{
                                                    width: '6px', height: '6px', borderRadius: '50%',
                                                    background: '#888', display: 'inline-block',
                                                    animation: `chatDot 1.2s ease-in-out ${i * 0.2}s infinite`,
                                                }} />
                                            ))}
                                        </span>
                                        : <>
                                            {msg.isNotification && (
                                                <i className="fa fa-bell" style={{
                                                    color: '#1976d2',
                                                    marginRight: '6px',
                                                    fontSize: '12px',
                                                }} />
                                            )}
                                            {renderMessageText(msg.text, msg.isNotification)}
                                        </>
                                    }
                                </div>
                                {!msg.typing && msg.timestamp && (
                                    <div style={{
                                        fontSize: '10px',
                                        color: '#888',
                                        paddingLeft: '4px',
                                        paddingRight: '4px',
                                    }}>
                                        {formatTimestamp(msg.timestamp)}
                                    </div>
                                )}
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    <div style={{
                        borderTop: '1px solid #e9ecef', padding: '10px 12px',
                        display: 'flex', gap: '8px', alignItems: 'flex-end', flexShrink: 0,
                    }}>
                        <textarea
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={wsStatus === 'ready' ? 'Ask something… (Enter to send)' : statusLabel}
                            disabled={wsStatus !== 'ready'}
                            rows={1}
                            style={{
                                flex: 1, resize: 'none', border: '1px solid #dee2e6',
                                borderRadius: '8px', padding: '8px 10px',
                                fontSize: '13px', lineHeight: 1.4,
                                outline: 'none', fontFamily: 'inherit',
                                maxHeight: '80px', overflowY: 'auto',
                                background: wsStatus !== 'ready' ? '#f8f9fa' : '#fff',
                            }}
                        />
                        <button
                            onClick={sendMessage}
                            disabled={wsStatus !== 'ready' || !input.trim()}
                            style={{
                                background: 'transparent', border: 'none',
                                color: '#6b32b4', padding: '8px', cursor: 'pointer',
                                fontSize: '18px', flexShrink: 0, lineHeight: 1,
                                opacity: (wsStatus !== 'ready' || !input.trim()) ? 0.3 : 1,
                                transition: 'opacity 0.2s',
                            }}
                        >
                            <i className="fa fa-paper-plane" />
                        </button>
                    </div>

                    {/* Resize handles */}
                    {!isMaximized && (
                        <>
                            {/* Corner handles */}
                            <div
                                onMouseDown={(e) => handleMouseDownResize(e, 'top-left')}
                                style={{
                                    position: 'absolute', top: 0, left: 0,
                                    width: '12px', height: '12px',
                                    cursor: 'nw-resize',
                                    zIndex: 10,
                                }}
                            />
                            <div
                                onMouseDown={(e) => handleMouseDownResize(e, 'top-right')}
                                style={{
                                    position: 'absolute', top: 0, right: 0,
                                    width: '12px', height: '12px',
                                    cursor: 'ne-resize',
                                    zIndex: 10,
                                }}
                            />
                            <div
                                onMouseDown={(e) => handleMouseDownResize(e, 'bottom-left')}
                                style={{
                                    position: 'absolute', bottom: 0, left: 0,
                                    width: '12px', height: '12px',
                                    cursor: 'sw-resize',
                                    zIndex: 10,
                                }}
                            />
                            <div
                                onMouseDown={(e) => handleMouseDownResize(e, 'bottom-right')}
                                style={{
                                    position: 'absolute', bottom: 0, right: 0,
                                    width: '12px', height: '12px',
                                    cursor: 'se-resize',
                                    zIndex: 10,
                                }}
                            />
                            {/* Edge handles */}
                            <div
                                onMouseDown={(e) => handleMouseDownResize(e, 'top')}
                                style={{
                                    position: 'absolute', top: 0, left: '12px', right: '12px',
                                    height: '4px',
                                    cursor: 'n-resize',
                                    zIndex: 10,
                                }}
                            />
                            <div
                                onMouseDown={(e) => handleMouseDownResize(e, 'bottom')}
                                style={{
                                    position: 'absolute', bottom: 0, left: '12px', right: '12px',
                                    height: '4px',
                                    cursor: 's-resize',
                                    zIndex: 10,
                                }}
                            />
                            <div
                                onMouseDown={(e) => handleMouseDownResize(e, 'left')}
                                style={{
                                    position: 'absolute', left: 0, top: '12px', bottom: '12px',
                                    width: '4px',
                                    cursor: 'w-resize',
                                    zIndex: 10,
                                }}
                            />
                            <div
                                onMouseDown={(e) => handleMouseDownResize(e, 'right')}
                                style={{
                                    position: 'absolute', right: 0, top: '12px', bottom: '12px',
                                    width: '4px',
                                    cursor: 'e-resize',
                                    zIndex: 10,
                                }}
                            />
                        </>
                    )}
                </div>
            )}

            {/* Typing animation keyframes and drag styles */}
            <style>{`
                @keyframes chatDot {
                    0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
                    40% { transform: scale(1); opacity: 1; }
                }
                ${(isDragging || isResizing) ? `
                    * {
                        user-select: none !important;
                        -webkit-user-select: none !important;
                        -moz-user-select: none !important;
                        -ms-user-select: none !important;
                    }
                ` : ''}
            `}</style>
        </>
    );
}
